import { useNewCode } from '@calendly/feature-check-react'
import { isEmpty } from 'lodash-es'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { setGetParams } from 'actions/core/navigation'
import { TRACKING_EVENT_NAMES, useTrack, useTrackOnce } from 'hooks/analytics_events'

/** Positive 1-based askIndex only — matches deep-link parseAskIndex lower bound (range checked when suggestions load). */
const normalizeAskIndexParam = (raw) => {
  if (isEmpty(raw)) return null
  const index = Number(raw)
  if (!Number.isInteger(index) || index < 1) return null
  return raw
}

export const dispatchSegmentEvent = (track, user, eventUuid, isCalendlyMeeting, step, miEventUuid, options = {}) => {
  track({
    name: TRACKING_EVENT_NAMES.AI_NOTETAKER_ENGAGED,
    properties: {
      step,
      attendee_count: null,
      duration_in_minutes: null,
      transcript_generation_in_seconds: null,
      is_calendly_meeting: isCalendlyMeeting ?? true,
      user_uuid: user?.uuid,
      event_uuid: eventUuid,
      mi_event_uuid: miEventUuid,
      ...options
    }
  })
}

export const useSegment = (path, user, eventUuid, isCalendlyMeeting, step, miEventUuid) => {
  const location = useLocation()
  const track = useTrack()

  useEffect(() => {
    if (path === location.pathname) {
      dispatchSegmentEvent(
        track,
        user,
        eventUuid,
        isCalendlyMeeting,
        step,
        miEventUuid,
      )
    }
  }, [eventUuid, isCalendlyMeeting, location.pathname, miEventUuid, path, step, track, user])
}

export function useRecapEmailCTACheck(location, user, eventUuid, isCalendlyMeeting, miEventUuid) {
  const dispatch = useDispatch()
  // useFlag is false while Statsig is still loading — wait for a resolved key before classify+track.
  const { enabled: isAskCallieRecapsPageIntegrationEnabled, variationKey } = useNewCode(
    'ask_callie_recaps_page_integration',
  )
  const isCallieFlagResolved = variationKey != null
  const params = new URLSearchParams(location.search)
  const source = params.get('source')
  const askNT = params.get('askNotetaker')
  // `URLSearchParams.get` returns '' for bare `?ask=` / `?askIndex=` — treat as absent.
  const rawAsk = params.get('ask')
  const askNTQuery = isEmpty(rawAsk) ? null : rawAsk
  const askIndex = normalizeAskIndexParam(params.get('askIndex'))
  const pane = params.get('pane')
  const shareModal = params.get('show_share_modal')
  const hasCallieQuestion = askIndex != null || askNTQuery != null
  const hasCallieEmailEntry = (
    pane === 'ai_chat'
    || askIndex != null
    || askNT === 'True'
    || askNTQuery != null
  )

  let action
  if (shareModal === 'True') {
    action = 'share_recording_cta'
  } else if (
    isAskCallieRecapsPageIntegrationEnabled
    && hasCallieEmailEntry
  ) {
    action = hasCallieQuestion
      ? 'ask_callie_ai_question_cta'
      : 'ask_callie_cta'
  } else if (askNT === 'True') {
    action = askNTQuery ? 'ask_notetaker_ai_question_cta' : 'ask_notetaker_cta'
  } else {
    action = 'watch_recording_cta'
  }

  const isEmailSource = source === 'email'
  useTrackOnce(isEmailSource && isCallieFlagResolved, {
    name: TRACKING_EVENT_NAMES.MEETING_RECAP_INTERACTION,
    properties: {
      action,
      location: 'recap_email',
      event_uuid: eventUuid,
      mi_event_uuid: miEventUuid,
      is_calendly_meeting: isCalendlyMeeting,
      is_calendly_user: true,
      is_owner_session: true,
      is_preview: false
    }
  })

  // Flag-on only: clear source after classify/track so durable pane=ai_chat does not
  // re-fire on refresh. Flag-off leaves source for legacy AskNotetakerPanel, which
  // snapshots it for recap_email location attribution and clears it itself.
  useEffect(() => {
    if (!isEmailSource || !isAskCallieRecapsPageIntegrationEnabled) return
    dispatch(setGetParams({ source: null }))
  }, [dispatch, isAskCallieRecapsPageIntegrationEnabled, isEmailSource])
}
